import { HttpBackend, HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, finalize, map, of, shareReplay, tap } from 'rxjs';

import { environment } from '../../../environments/environment';
import { SupplierPortalSessionDTO } from '../dtos/supplier-portal/supplier-portal.model';

const AUTH_SESSION_STORAGE_KEY = 'cms-supplier.auth.session';
const AUTH_TOKEN_STORAGE_KEY = 'cms-supplier.auth.token';
const AUTH_STORAGE_KEY = 'ccms.supplier.auth';
const AUTH_TAB_HANDOFF_STORAGE_KEY = 'ccms.supplier.auth.tab-handoff';
const ACTIVITY_REFRESH_INTERVAL_MS = 15 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class SupplierAuthService {
  private router = inject(Router);
  private sessionState = signal<SupplierPortalSessionDTO | null>(null);
  private sessionTimerId: ReturnType<typeof setTimeout> | null = null;
  private lastActivityRefreshAt = 0;
  private refreshRequest$: Observable<SupplierPortalSessionDTO> | null = null;
  private http: HttpClient;

  session = computed(() => this.sessionState());
  isAuthenticated = computed(() => {
    const session = this.sessionState();

    return !!session?.accessToken && !this.isExpired(session.accessTokenExpiresAtUtc);
  });

  constructor(httpBackend: HttpBackend) {
    this.http = new HttpClient(httpBackend);
    this.clearLegacyStorage();
    this.startActivityTracking();
  }

  get accessToken() {
    const session = this.sessionState();

    return session?.accessToken && !this.isExpired(session.accessTokenExpiresAtUtc)
      ? session.accessToken
      : null;
  }

  restoreSession() {
    return this.refreshSession().pipe(
      map(() => true),
      catchError(() => {
        this.clearSessionState();
        return of(false);
      }),
    );
  }

  setSession(session: SupplierPortalSessionDTO) {
    this.sessionState.set(session);
    this.lastActivityRefreshAt = Date.now();
    this.clearLegacyStorage();
    this.scheduleSessionTimeout();
  }

  recordActivity() {
    const session = this.sessionState();

    if (!session?.accessToken) return;

    const now = Date.now();
    if (now - this.lastActivityRefreshAt < ACTIVITY_REFRESH_INTERVAL_MS) return;

    this.lastActivityRefreshAt = now;
    this.refreshSession().subscribe({
      error: () => this.logout(this.router.url),
    });
  }

  refreshSession() {
    if (this.refreshRequest$) return this.refreshRequest$;

    this.refreshRequest$ = this.http
      .post<SupplierPortalSessionDTO>(
        `${environment.apiBaseUrl}/supplier-portal/refresh-session`,
        {},
        { withCredentials: true },
      )
      .pipe(
        tap((res) => this.setSession(res)),
        finalize(() => (this.refreshRequest$ = null)),
        shareReplay({ bufferSize: 1, refCount: false }),
      );

    return this.refreshRequest$;
  }

  logout(returnUrl?: string) {
    const sanitizedReturnUrl = this.sanitizeReturnUrl(returnUrl);

    this.clearSessionState();
    this.http
      .post<void>(`${environment.apiBaseUrl}/supplier-portal/logout`, {}, { withCredentials: true })
      .pipe(catchError(() => of(null)))
      .subscribe();
    this.router.navigate(['/login'], {
      queryParams: sanitizedReturnUrl ? { returnUrl: sanitizedReturnUrl } : undefined,
    });
  }

  clearSession() {
    this.clearSessionState();
  }

  handleUnauthorized(returnUrl?: string) {
    this.logout(returnUrl ?? this.router.url);
  }

  private clearSessionState() {
    this.sessionState.set(null);
    this.lastActivityRefreshAt = 0;
    this.clearLegacyStorage();

    if (this.sessionTimerId) {
      clearTimeout(this.sessionTimerId);
      this.sessionTimerId = null;
    }
  }

  private clearLegacyStorage() {
    localStorage.removeItem(AUTH_SESSION_STORAGE_KEY);
    localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
    localStorage.removeItem(AUTH_STORAGE_KEY);
    localStorage.removeItem(AUTH_TAB_HANDOFF_STORAGE_KEY);
    sessionStorage.removeItem(AUTH_SESSION_STORAGE_KEY);
    sessionStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
    sessionStorage.removeItem(AUTH_STORAGE_KEY);
  }

  private isExpired(expiresAtUtc?: string | null) {
    if (!expiresAtUtc) {
      return true;
    }

    return new Date(expiresAtUtc).getTime() <= Date.now();
  }

  private scheduleSessionTimeout() {
    if (this.sessionTimerId) {
      clearTimeout(this.sessionTimerId);
      this.sessionTimerId = null;
    }

    const expiresAt = this.sessionState()?.accessTokenExpiresAtUtc;
    if (!expiresAt) return;

    const delay = Math.max(new Date(expiresAt).getTime() - Date.now(), 0);
    this.sessionTimerId = setTimeout(() => this.logout(this.router.url), delay);
  }

  private startActivityTracking() {
    const events = ['click', 'keydown', 'pointerdown', 'submit', 'focus'];

    for (const eventName of events) {
      window.addEventListener(eventName, () => this.recordActivity(), { passive: true });
    }

    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.recordActivity();
      }
    });
  }

  private sanitizeReturnUrl(returnUrl?: string | null) {
    if (
      !returnUrl ||
      returnUrl === '/' ||
      !returnUrl.startsWith('/') ||
      returnUrl.startsWith('//') ||
      returnUrl.startsWith('/login')
    ) {
      return null;
    }

    return returnUrl;
  }
}
